<template>
  <BaseModule
    v-bind="$props"
    class="mosaic-with-text"
    data-module="mosaic-with-text"
  >
    <div
      class="mosaic-with-text__container"
      :style="{
        '--text-position': textPosition,
      }"
    >
      <div class="mosaic-with-text__content">
        <header
          v-if="title || subtitle"
          class="base-title-fontsize"
        >
          <component
            :is="titleTag"
            v-if="title"
            class="tit"
          >
            {{ title }}
          </component>
          <h3
            v-if="subtitle"
            class="sub"
          >
            {{ subtitle }}
          </h3>
        </header>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
        <footer
          v-if="buttons.length"
          class="mosaic-with-text__buttons"
        >
          <ul>
            <li
              v-for="(button, index) in buttons"
              :key="index"
            >
              <EditorLink
                v-if="button.buttonText"
                :model="button.buttonUrl"
                class="cta button"
                :class="{ 'button--featured': button.buttonFeatured }"
              >
                {{ button.buttonText }}
              </EditorLink>
            </li>
          </ul>
        </footer>
      </div>
      <div class="mosaic-with-text__mosaic">
        <ul
          v-if="mosaic.length > 0"
          :class="`--${mosaicLength}-images`"
        >
          <li
            v-for="(item, idx) in mosaic"
            :key="item.id || idx"
            :class="`item-${idx + 1}`"
          >
            <LImage
              :src="item.image"
              :alt="item.altText"
            />
          </li>
        </ul>
        <Warning
          v-else
          text="Nenhuma imagem adicionada"
        >
        </Warning>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import base from '@/mixins/base';
import Warning from '@/components/Warning.vue';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

export default {
  name: 'MosaicWithText',
  components: {
    BaseModule,
    EditorLink,
    Warning,
  },
  mixins: [base],
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    textPosition: {
      type: String,
      default: 'row',
    },
    mosaic: {
      type: Array,
      default() {
        return [];
      },
    },
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    mosaicLength() {
      return this.mosaic.length;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
              {
                name: 'textPosition',
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'row',
                  },
                  {
                    label: 'Direita',
                    value: 'row-reverse',
                  },
                ],
              },
            ],
          },
          {
            name: 'mosaicImages',
            label: 'Mosaico',
            fields: [
              {
                name: 'mosaic',
                type: 'List',
                minItems: 1,
                maxItems: 3,
                itemLabel: 'Imagem {index}',
                addItemLabel: 'Adicionar imagem',
                fields: [
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message: 'Insira uma imagem',
                      },
                    },
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                  },
                ],
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Adicione uma imagem',
                  },
                },
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                type: 'List',
                name: 'buttons',
                maxItems: 2,
                itemLabel: 'Botão {index}',
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.mosaic-with-text {
  &__container {
    display: flex;
    gap: 6.25%;
    width: 87.5%;
    margin: auto;
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
    flex-direction: var(--text-position);
  }

  &__content,
  &__mosaic {
    width: 50%;
    margin: auto 0;
  }

  &__mosaic {
    ul {
      display: grid;
      align-items: center;
      gap: 24px;

      &.--3-images {
        grid-template-areas:
          'item1 item3'
          'item2 item3';

        .item-1,
        .item-2 {
          justify-self: end;

          .img {
            max-height: 260px;
          }
        }

        .item-1 {
          align-self: end;
        }

        .item-2 {
          align-self: start;
        }

        .item-3 {
          .img {
            max-height: 360px;
          }
        }
      }

      &.--2-images {
        grid-template-areas: 'item1 item2';

        .item-1,
        .item-2 {
          .img {
            max-height: 360px;
          }
        }

        .item-1 {
          justify-self: end;
        }

        .item-2 {
          justify-self: start;
        }
      }

      &.--1-images {
        grid-template-areas: 'item1';

        .item-1 {
          text-align: center;

          .img {
            max-width: 590px;
            max-height: 435px;
          }
        }
      }

      .item-1 {
        grid-area: item1;
      }

      .item-2 {
        grid-area: item2;
      }

      .item-3 {
        grid-area: item3;
      }

      li {
        .img {
          display: block;
          max-width: 290px;
          border-radius: 8px;
        }
      }
    }
  }

  &__buttons {
    ul {
      display: flex;
      flex-wrap: wrap;
      margin: -8px;
    }

    li {
      margin: 0 8px;
    }

    .button {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 48px;
      font-size: var(--content-size-desk, $size-normal);
      font-weight: $lb-normal;
      color: var(--button-color, $primary);
      line-height: 1.2;
      border: 2px solid var(--button-color, $primary);
      background-color: transparent;
      min-width: 200px;
      text-decoration: none;
      transition: 0.4s;
      margin-top: 24px;
      text-transform: inherit;

      &--featured {
        color: var(--button-text-color, $primary);
        background-color: var(--button-color, $primary);
        border: none;
      }
    }
  }
}

@include media-breakpoint-down(lg) {
  .mosaic-with-text {
    &__container {
      flex-direction: column;
      padding-top: var(--padding-top-mobile);
      padding-bottom: var(--padding-bottom-mobile);
    }

    &__content,
    &__mosaic {
      width: 100%;
    }

    &__content {
      padding: 0;
    }

    &__mosaic {
      margin-top: 30px;

      ul {
        gap: 12px;
      }
    }

    &__buttons {
      ul {
        flex-direction: column;
      }

      .button {
        font-size: var(--content-size-mobile, $size-normal);
      }
    }
  }
}
</style>
